import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { apiService } from "../services/api";
import "./AssessmentAttempts.css";

const AssessmentAttempts = () => {
  const { assessmentId } = useParams();
  const [attempts, setAttempts] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch assessment details and attempts in parallel
        const [assessmentData, attemptsData] = await Promise.all([
          apiService.assessments.getAssessment(assessmentId),
          apiService.assessments.getAssessmentAttempts(assessmentId),
        ]);

        if (assessmentData) {
          setAssessment(assessmentData);
        }

        if (attemptsData) {
          setAttempts(attemptsData);
        }
      } catch (err) {
        console.error("Error fetching assessment attempts:", err);
        setError("Failed to load assessment attempts");
      } finally {
        setLoading(false);
      }
    };

    if (assessmentId) {
      fetchData();
    }
  }, [assessmentId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "N/A";
    if (seconds === 0) return "< 1s";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  const getStatusBadge = (status, percentage) => {
    if (
      status === "completed" ||
      percentage >= (assessment?.passingScore || 70)
    ) {
      return <span className="status-badge passed">Passed</span>;
    } else if (
      status === "failed" ||
      percentage < (assessment?.passingScore || 70)
    ) {
      return <span className="status-badge failed">Failed</span>;
    } else {
      return <span className="status-badge in-progress">In Progress</span>;
    }
  };

  if (loading) {
    return (
      <div className="assessment-attempts-container">
        <div className="loading">Loading assessment attempts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-attempts-container">
        <div className="error-message">{error}</div>
        <Link to="/dashboard" className="back-link">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="assessment-attempts-container">
      <div className="attempts-header">
        <Link to="/dashboard" className="back-link">
          ← Back to Dashboard
        </Link>
        <h1>Assessment Attempts</h1>
        {assessment && (
          <div className="assessment-info">
            <h2>{assessment.title}</h2>
            <p className="assessment-description">{assessment.description}</p>
            <div className="assessment-meta">
              <span className="meta-item">
                <strong>Skill:</strong> {assessment.skillName}
              </span>
              <span className="meta-item">
                <strong>Difficulty:</strong> {assessment.difficultyLevel}
              </span>
              <span className="meta-item">
                <strong>Passing Score:</strong> {assessment.passingScore}%
              </span>
              <span className="meta-item">
                <strong>Max Score:</strong> {assessment.maxScore}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="attempts-content">
        {attempts.length === 0 ? (
          <div className="no-attempts">
            <p>No attempts found for this assessment.</p>
            <Link
              to={`/assessment/${assessmentId}`}
              className="take-assessment-btn"
            >
              Take Assessment
            </Link>
          </div>
        ) : (
          <div className="attempts-list">
            <h3>Your Attempts ({attempts.length})</h3>
            <div className="attempts-table">
              <div className="table-header">
                <div className="header-cell">Attempt</div>
                <div className="header-cell">Score</div>
                <div className="header-cell">Percentage</div>
                <div className="header-cell">Status</div>
                <div className="header-cell">Date</div>
                <div className="header-cell">Duration</div>
              </div>
              {attempts.map((attempt) => (
                <div key={attempt.id} className="table-row">
                  <div className="table-cell">#{attempt.attemptNumber}</div>
                  <div className="table-cell">
                    {attempt.score}/{attempt.maxScore}
                  </div>
                  <div className="table-cell">{attempt.percentage}%</div>
                  <div className="table-cell">
                    {getStatusBadge(attempt.status, attempt.percentage)}
                  </div>
                  <div className="table-cell">
                    {formatDate(attempt.completedAt || attempt.startedAt)}
                  </div>
                  <div className="table-cell">
                    {formatDuration(attempt.duration)}
                  </div>
                </div>
              ))}
            </div>

            <div className="attempts-actions">
              <Link
                to={`/assessment/${assessmentId}`}
                className="retake-assessment-btn"
              >
                Retake Assessment
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentAttempts;
